import React, { Component, Fragment } from "react";
import { connect } from "react-redux"; import {   setOnboardingStatusFromLocalStorage, updateWelcomeLinkOptions, } from "../modules/rtk/onboarding/onboardingSlice"; import { parseParams } from "../functions/urlHelper"; import {   Box, Button, Card, CardActions, CardContent, CardHeader, Divider, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { AppendIfPrefixExists } from "../modules/sitePaths";
import ForwardNavLink from "../components/ForwardRefNavLink";
import { getWelcomeProgressLocalStorage } from "../auth/WelcomeHelpers";
import { AccountWelcomeLinkDto } from "../lib/ShiOneClient";
import type { MicrosoftSSOResult } from "../modules/rtk/onboarding/onboardingTypes";
import type { RootState } from "../store";
import type { Location, NavigateFunction } from "react-router-dom";
import type { AppDispatch } from "../store";

interface OnboardingState {
  onboardingRequestStatus: string;
}

interface WelcomeCallbackOwnProps {
  navigate: NavigateFunction;
  location: Location;
}

interface WelcomeCallbackStateProps {
  onboarding: OnboardingState;
}

interface WelcomeCallbackDispatchProps {
  setOnboardingFromApplicationStorage: (authResult: MicrosoftSSOResult) => void;
  setWelcomeLinkOptions: (welcomeLink: AccountWelcomeLinkDto) => void;
}

type WelcomeCallbackProps = WelcomeCallbackOwnProps & WelcomeCallbackStateProps & WelcomeCallbackDispatchProps;

const styles = {
  grow: {
    flexGrow: 1,
  },
  card: {
    padding: 0,
  },
  titleNoMargin: {
    fontWeight: 400,
    fontSize: "1rem",
    textTransform: "uppercase",
  },
  alert: {
    boxShadow: "none !important",
    border: "none !important",
    "& > .MuiAlert-message": {
      minWidth: "60%",
    },
    "& > .MuiAlert-message > .MuiTypography-root": {
      color: "inherit",
    },
    marginBottom: "1rem",
  },
};

const asString = (val: string | string[] | null | undefined): string | undefined =>
  typeof val === "string" ? val : Array.isArray(val) ? val[0] : undefined;

const callbackResult = (params: Record<string, string | string[] | null>): MicrosoftSSOResult => {
  const result: MicrosoftSSOResult = {
    success: false,
    welcome_guid: "",
  };
  const state = asString(params.state);
  if (state) {
    result.welcome_guid = state;
  }
  if (params.admin_consent) {
    result.success = true;
  }
  const error = asString(params.error);
  const errorSubcode = asString(params.error_subcode);
  const errorDescription = asString(params.error_description);
  if (error && errorSubcode) {
    if (error === "access_denied" && errorSubcode === "cancel") {
      result.error = error;
      result.reason =
        "Either you did not sign in with an administrator account, or you declined consent.";
    }
  }
  if (error && errorDescription) {
    result.error = error;
    result.reason = errorDescription;
  }
  return result;
};

class WelcomeCallback extends Component<WelcomeCallbackProps> {
  constructor(props: WelcomeCallbackProps) {
    super(props);

    this.completeWelcomeLink = this.completeWelcomeLink.bind(this);
  }

  completeWelcomeLink = (guid: string) => {
    const { setWelcomeLinkOptions } = this.props;

    setWelcomeLinkOptions(new AccountWelcomeLinkDto({
      linkId: guid,
      completed: true,
      microsoftSSOSelected: true,
      microsoftSSOSuccessful: true,
    }));
  };

  render() {
    const {
      navigate,
      location,
      setOnboardingFromApplicationStorage,
      onboarding,
    } = this.props;
    const existingWelcomeStatus = getWelcomeProgressLocalStorage();
    const authResult = callbackResult(parseParams(location.search) as Record<string, string | string[] | null>);

    if (
      authResult.welcome_guid &&
      onboarding.onboardingRequestStatus === "idle" &&
      existingWelcomeStatus.accountId !== -1
    ) {
      setOnboardingFromApplicationStorage(authResult);
    }

    if (
      authResult.welcome_guid &&
      onboarding.onboardingRequestStatus === "complete" &&
      existingWelcomeStatus.accountId !== -1
    ) {
      navigate(`/welcome/${authResult.welcome_guid}`);
    }

    if (authResult && existingWelcomeStatus.accountId === -1) {
      return (
        <Fragment>
          <Box bgcolor={"background.default"}>
            <Card className="wizard-body" sx={{ py: 3, px: 5 }}>
              <CardHeader
                title={
                  <Typography
                    variant="body1"
                    component="div"
                    color="textSecondary"
                    sx={styles.titleNoMargin}
                  >
                    SHI One Organization Registration
                  </Typography>
                }
                subheader={
                  <Typography variant="caption" component="div">
                    {authResult.success ? "Finish Registration" : "Error"}
                  </Typography>
                }
              />
              <Divider />
              <CardContent>
                <Typography variant="body1" component="div">
                  {authResult.success ? (
                    <Alert severity="success" sx={styles.alert}>
                      <AlertTitle>Success</AlertTitle>
                      <p>
                        Your tenant has been authorized for Microsoft Single
                        Sign-on.
                      </p>
                    </Alert>
                  ) : (
                    <Alert severity="error" sx={styles.alert}>
                      <p>{authResult.reason}</p>
                      <p>
                        Your tenant was not authorized for Microsoft Single
                        Sign-on due to the above reason.
                      </p>
                      <p>
                        In order to sign in to SHI One using your Microsoft
                        credentials, an admin must successfully consent to the
                        SHI One application via the consent link you were
                        provided.
                      </p>
                      <p>
                        Please try again, or contact your account executive for
                        assistance.
                      </p>
                    </Alert>
                  )}
                  {authResult.success && (
                    <p>
                      Click finish to proceed to SHI One and login with your
                      Microsoft Account.
                    </p>
                  )}
                </Typography>
              </CardContent>
              {authResult.success && (
                <Fragment>
                  <Divider />
                  <CardActions>
                    <Grid
                      container
                      sx={styles.grow}
                      spacing={3}
                      justifyContent="flex-end"
                    >
                      <Grid >
                        <Button
                          color="primary"
                          component={ForwardNavLink}
                          onClick={() =>
                            this.completeWelcomeLink(authResult.welcome_guid!)
                          }
                          to={AppendIfPrefixExists("/")}
                        >
                          Finish
                        </Button>
                      </Grid>
                    </Grid>
                  </CardActions>
                </Fragment>
              )}
            </Card>
          </Box>
        </Fragment>
      );
    }

    return <div>Loading...</div>;
  }
}

const mapStateToProps = (state: RootState) => {
  const { onboarding } = state;
  return {
    onboarding,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setOnboardingFromApplicationStorage: (authResult: MicrosoftSSOResult) =>
      dispatch(setOnboardingStatusFromLocalStorage(authResult)),
    setWelcomeLinkOptions: (welcomeLink: AccountWelcomeLinkDto) =>
      dispatch(updateWelcomeLinkOptions(welcomeLink)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WelcomeCallback);
